/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import styled from "@emotion/styled";
import { AnimatePresence, motion } from "framer-motion";
import { nanoid } from "nanoid";
import { useState } from "react";
import ReactTooltip from "react-tooltip";
import { ICampaignsUsedInTags } from "../../../../api/Tags";
import TriangleAlert from "../../../../assets/icon/triangle-alert";
import { ICategoryType } from "../../../../generated/Api";
import { customTooltip } from "../../../../styles/general";
import { ITag } from "../../../../types/TagTypes";
import useDebounce from "../../../../utils/UseDebounce";
import StyledCheckBox from "../../../StyledCheckBox";
import Tag from "../../../Tag";

interface TableProps {
  check: boolean;
}

interface GridRowProps {
  restricted: boolean;
}

const Row = styled(motion.div)<TableProps>`
  display: grid;
  border: 1px solid;
  border-color: ${(props) => (props.check ? "#FB972E" : "#eaeaeabf")};
  background-color: ${(props) =>
    props.check ? "rgb(251, 151, 46, 0.05)" : "#ffffff"};
  grid-template-rows: 1fr;
  align-items: center;
`;

const GridRow = styled(motion.div)<GridRowProps>`
  width: 100%;
  height: 60px;

  background-color: #ffffff;

  display: grid;

  grid-template-columns: 1fr 0.6fr 1fr;

  grid-column-gap: 24px;

  padding: 15px 25px;

  cursor: ${(props) => (props.restricted ? "default" : "pointer")};

  align-items: center;
  justify-content: space-between;

  @media screen and (max-width: 1010px) {
    width: fit-content;
  }
`;

const TableTitle = styled.div`
  color: #2f2f2f;
  display: flex;
  font-size: 16px;
  align-items: center;
  /* overflow: hidden; */
  max-width: 100%;
`;

const TextCss = css`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  display: block;
`;

const userCss = css`
  ${TextCss}
  cursor: pointer;
  :hover {
    opacity: 0.6;
  }
`;

const WrapperCheckboxAndTitle = styled.div<{ hasCampaign: boolean }>`
  display: flex;
  align-items: center;
  justify-content: start;

  .css-17ninii {
    border: ${(props) => props.hasCampaign && "1px solid #c40000 !important"};
    background: ${(props) =>
      props.hasCampaign && "#f8f8f8 0% 0% no-repeat padding-box"};
  }
`;

const CampaignsUsedTag = styled.div`
  width: fit-content;
  background-color: #f2f2f2;
  color: #2f2f2f;
  position: relative;

  max-width: 100%;
  border-radius: 4px;
  max-height: 32px;
  padding: 6px 11px;
  font-size: 15px;
  margin: 0px 4px;
  transition: background-color 0.3s ease-in-out;

  &:first-child {
    margin-left: 0px;
  }

  &:hover {
    background-color: #dddddd;
  }
`;

const TextContainer = styled.div`
  text-overflow: ellipsis;
  max-width: 85px;
  overflow: hidden;
  white-space: nowrap;
`;

const ContainerCampaignsUsedTag = styled.div`
  display: flex;
`;

const TooltipText = styled.span`
  font-size: 14px;
  letter-spacing: 0px;
  color: #fff;
  text-align: center;
`;

const TooltipTextMore = styled.span`
  font-size: 14px;
  letter-spacing: 0px;
  color: #333;
  text-align: center;
`;

const Divider = styled.div`
  margin: 14px 0px;
  border-bottom: 1px solid #e2e2e2;
  width: 100%;
`;

interface Props {
  row: ITag;
  isChecked: boolean;
  handleCheck: () => void;
  campaignsUsedInTag: ICampaignsUsedInTags;
}

export default function TableRow(props: Props): JSX.Element {
  const { row, handleCheck, isChecked, campaignsUsedInTag } = props;
  const nanoId = nanoid();
  const [isOpenDropView, setIsOpenDropView] = useState(false);
  const isOpenDropViewDebounced = useDebounce(isOpenDropView, 200);
  const rowCampaignsUsedInTag =
    campaignsUsedInTag?.campaignsUsedIn.map((campaign) => campaign.name) || [];

  return (
    <Row
      check={!rowCampaignsUsedInTag.length && isChecked}
      layout
      css={css`
        margin-top: 7px;
      `}
    >
      <AnimatePresence>
        <GridRow
          layout
          restricted={!!rowCampaignsUsedInTag.length}
          onClick={() => {
            !rowCampaignsUsedInTag.length && handleCheck();
          }}
        >
          <WrapperCheckboxAndTitle hasCampaign={!!rowCampaignsUsedInTag.length}>
            {!rowCampaignsUsedInTag.length ? (
              <StyledCheckBox check={isChecked} handleCheck={handleCheck} />
            ) : (
              <div style={{ marginLeft: "3.5px", marginRight: "2px" }}>
                <TriangleAlert />
              </div>
            )}
            <Tag
              customStyle={{ margin: "0px 0px 0px 16px", maxWidth: "100px" }}
              tag={{
                categoryId: "",
                createdAt: new Date(),
                name: row.name,
                userUid: "",
                type:
                  row.type === "system"
                    ? ICategoryType.System
                    : ICategoryType.User,
                category: row.category,
                _id: row._id,
              }}
            />
          </WrapperCheckboxAndTitle>

          <TableTitle css={TextCss}>{row.contactCount || 0}</TableTitle>

          <TableTitle>
            <ContainerCampaignsUsedTag>
              {!!rowCampaignsUsedInTag.length ? (
                <CampaignsUsedTag data-tip data-for={nanoId} id="tag-container">
                  <TextContainer>{rowCampaignsUsedInTag[0]}</TextContainer>

                  <ReactTooltip
                    id={nanoId}
                    place="top"
                    effect="solid"
                    backgroundColor="#333333"
                    css={customTooltip}
                  >
                    <TooltipText>{rowCampaignsUsedInTag[0]}</TooltipText>
                  </ReactTooltip>
                </CampaignsUsedTag>
              ) : (
                "-"
              )}
              {!!rowCampaignsUsedInTag.length &&
                rowCampaignsUsedInTag.length > 1 && (
                  <CampaignsUsedTag
                    onMouseEnter={() => setIsOpenDropView(true)}
                    onMouseLeave={() => setIsOpenDropView(false)}
                    data-tip
                    data-for={`${nanoId}-more`}
                  >
                    <TextContainer>
                      {`+${rowCampaignsUsedInTag.length - 1} more`}
                    </TextContainer>

                    {/* Popover with all campaigns used with this tag */}
                    {/* <CampaignListPopover
                      campaignsList={rowCampaignsUsedInTag}
                      isOpen={isOpenDropViewDebounced as boolean}
                      isOpen={true}
                      hasCopyToClipboard={false}
                      handleClose={() => setIsOpenDropView(false)}
                    /> */}

                    <ReactTooltip
                      id={`${nanoId}-more`}
                      place="bottom"
                      effect="solid"
                      backgroundColor="#fff"
                      css={css`
                        width: 250px;
                        max-height: 300px;
                        box-shadow: 0px 3px 20px #00000026;
                        border-radius: 5px;
                        padding: 0 !important;
                        opacity: 1 !important;
                      `}
                    >
                      <p
                        css={css`
                          color: #333333;
                          font-size: 16px;
                          font-weight: 600;
                          padding-left: 25px;
                        `}
                      >
                        Campaigns used in
                      </p>
                      <Divider />
                      <div
                        css={css`
                          display: flex;
                          flex-direction: column;
                          margin-left: 25px !important;
                          margin-bottom: 6px;
                          overflow-y: scroll;
                        `}
                      >
                        {!!rowCampaignsUsedInTag.length &&
                          rowCampaignsUsedInTag.slice(1).map((campaignName) => (
                            <CampaignsUsedTag
                              css={css`
                                margin: 0px 0px 8px 0px;
                              `}
                            >
                              <TextContainer
                                css={css`
                                  max-width: 175px;
                                `}
                              >
                                {campaignName}
                              </TextContainer>
                            </CampaignsUsedTag>
                          ))}
                      </div>
                    </ReactTooltip>
                  </CampaignsUsedTag>
                )}
            </ContainerCampaignsUsedTag>
          </TableTitle>
        </GridRow>
      </AnimatePresence>
    </Row>
  );
}
